<script setup lang="ts">
import { EButton, EDropdownMenu, ETooltip, type MenuItem } from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import AppIcon from '@/components/AppIcon.vue'
import { LOCALE_NAMES, SUPPORTED_LOCALES, setLocale, type AppLocale } from '@/i18n'

/**
 * The language switch (CLAUDE.md §6: `en` complete, `dv` secondary, RTL real).
 *
 * It changes the locale for this browser immediately — message catalogue,
 * `<html lang>` and `<html dir>` together. The signed-in user's saved
 * preference still lives on their profile; this is the one-click version for
 * someone who wants to read the current screen in the other language without
 * going to Profile first.
 *
 * Language names are endonyms and are never translated: a picker that offers
 * "Dhivehi" to a Dhivehi speaker is a picker they cannot use.
 */

const { locale, t } = useI18n()

const items = computed<MenuItem[]>(() =>
  SUPPORTED_LOCALES.map((code) => ({
    id: code,
    label: LOCALE_NAMES[code],
    ...(locale.value === code ? { shortcut: t('shell.locale.current') } : {}),
  })),
)

function onSelect(item: MenuItem): void {
  setLocale(item.id as AppLocale)
}
</script>

<template>
  <EDropdownMenu :items="items" :label="$t('shell.locale.menuLabel')" @select="onSelect">
    <template #trigger>
      <span>
        <ETooltip :content="$t('shell.locale.menuLabel')">
          <EButton variant="ghost" size="sm" :aria-label="$t('shell.locale.trigger')">
            <template #icon><AppIcon name="globe" :size="15" /></template>
            <span class="locale-menu__code">{{ locale.toUpperCase() }}</span>
          </EButton>
        </ETooltip>
      </span>
    </template>
  </EDropdownMenu>
</template>

<style>
.locale-menu__code {
  font-size: var(--erpx-text-2xs);
  font-weight: var(--erpx-weight-semibold);
  letter-spacing: var(--erpx-tracking-wide);
}
</style>
